import { useAddItemToCart } from 'hooks/cart';
import { useCallback, useState } from 'react';
import {
  AddToCartCustomOption,
  Cart,
  CustomizableOptionValueInterface,
  ProductInterface,
  ProductTypeEnum,
} from 'types/types';
import resourceUrl from '../../../../util/make-url';
import {
  generateVariantIdFromSelectedOption,
  mapCartItemToPayload,
  mapProductToPayload,
  trackCartEvent,
} from '../../../../util/tag-manager';
import { isLimitedTimeDeal } from '../../../../util/products';
import { useAddedToCartNotification } from 'hooks/product';
import AddedToCartNotificationBody from 'components/_shared/widgets/product/added-to-cart-notification-body';
import { actionToStepMap, EventNamesEnum } from 'types/third-party/gtm';
import { useCustomer } from 'hooks/customer';
import { useRouterPush } from 'components/configurable-routing';

export const useQuickAddToCart = ({
  product,
  onComplete,
}: {
  product: ProductInterface;
  onComplete: () => void;
}) => {
  const addedToCartNotification = useAddedToCartNotification();
  const user = useCustomer();
  const push = useRouterPush();

  const [quantityAdded, setQuantityAdded] = useState(1);

  const [selectedOption, setSelectedOption] = useState<
    CustomizableOptionValueInterface | undefined
  >(undefined);

  const displayPrice = selectedOption?.price.fixed || product.price;
  const notificationImageUrl = resourceUrl(
    selectedOption?.image?.url || product.image.url,
    { width: 40 }
  );

  const onAddToCartComplete = useCallback(
    ({ cart, message }: { cart: Cart; message?: string }) => {
      const { items, totals, voucher } = cart;
      const vouchers = totals?.discount?.items;
      const trackEvent = () =>
        trackCartEvent({
          cartItems: items.map(mapCartItemToPayload),
          event: EventNamesEnum.beginCheckout,
          stepNumber: actionToStepMap[EventNamesEnum.beginCheckout],
          callback: () => push({ url: '/checkout' }),
          amounts: {
            amount: totals?.grandTotal.value,
            subTotal: totals?.subtotal.value,
            totalProductQuantity: items.reduce(
              (acc, item) => (acc += item.quantity),
              0
            ),
            voucherCode: voucher,
            voucherDiscount: (vouchers || []).reduce(
              (acc, voucher) => acc + voucher.amount.value,
              0
            ),
          },
        });
      addedToCartNotification({
        trackEventCallback: trackEvent,
        product,
        withContinueShoppingAction: false,
        body: (
          <AddedToCartNotificationBody
            imageUrl={notificationImageUrl}
            quantity={quantityAdded}
            price={displayPrice}
            message={message}
          />
        ),
      });
      trackCartEvent({
        product: mapProductToPayload({
          product,
          variantId: generateVariantIdFromSelectedOption(
            product.realId,
            selectedOption
          ),
        }),
        event: EventNamesEnum.addToCart,
        productQuantity: quantityAdded,
        user,
      });
      trackCartEvent({
        product: mapProductToPayload({
          product,
          variantId: generateVariantIdFromSelectedOption(
            product.realId,
            selectedOption
          ),
        }),
        event: EventNamesEnum.quickAddToCartAdded,
        productQuantity: quantityAdded,
        user,
      });

      onComplete();
    },
    [
      addedToCartNotification,
      displayPrice,
      notificationImageUrl,
      onComplete,
      product,
      push,
      quantityAdded,
      selectedOption,
      user,
    ]
  );

  const [addToCart, { loading, error }] = useAddItemToCart({
    onComplete: onAddToCartComplete,
    disableErrorToast: true,
  });

  const addToCartCallback = useCallback(
    ({
      quantity = 1,
      customOptions,
    }: {
      quantity?: number;
      customOptions?: AddToCartCustomOption[];
    } = {}) => {
      addToCart({
        type: ProductTypeEnum.Simple,
        sku: product.sku,
        quantity,
        customOptions,
      });
      setQuantityAdded(quantity);
    },
    [addToCart, product]
  );

  const trackOpen = useCallback(() => {
    trackCartEvent({
      product: mapProductToPayload({ product }),
      event: isLimitedTimeDeal(product)
        ? EventNamesEnum.quickAddToCartLTDClick
        : EventNamesEnum.quickAddToCartClick,
      user,
    });
  }, [product, user]);

  const trackClose = useCallback(() => {
    trackCartEvent({
      product: mapProductToPayload({ product }),
      event: EventNamesEnum.quickAddToCartOptionsClosed,
      user,
    });
  }, [product, user]);

  return {
    addToCartCallback,
    setSelectedOption,
    loading,
    trackOpen,
    trackClose,
    error,
  };
};
